import { ReactElement, useCallback, useEffect, useMemo, useState } from "react";
import LayoutCard from "../shared-ui-components/LayoutCard";
import "./TopSummaryCard.scss";
import {
  FileOutlined,
  FileUnknownOutlined,
  LayoutOutlined,
  QuestionCircleOutlined,
  CopyOutlined,
  ExclamationCircleOutlined,
} from "@ant-design/icons";
import InnerCard from "../shared-ui-components/InnerCard";
import UrlHtmlSummaryTab from "../analysis/components/UrlHtmlSummaryTab";
import ProgRing from "../shared-ui-components/ProgRing";
import {
  getVerdictbyFlag,
  summaryTitlesForVerdicts,
} from "../logic/business-rules";
import { ServerCategorizationsData } from "../models/data-structures";
import { toHumanReadableSize, toKebabCase } from "../util/general";
import ToolTip from "../shared-ui-components/ToolTip";

interface TopSummaryCardProps {
  jsonData: ServerCategorizationsData;
  isFavorite?: boolean;
  favoriteLoading?: boolean;
  onFavoriteToggle?: () => void;
}

export default function TopSummaryCard({
  jsonData,
  isFavorite = false,
  favoriteLoading = false,
  onFavoriteToggle,
}: TopSummaryCardProps): ReactElement {
  const fileAnalysisData = jsonData;

  // Defensive: If Objects is missing or empty, show a friendly message (but do NOT return early)
  const isSummaryDataAvailable =
    fileAnalysisData.Objects &&
    Array.isArray(fileAnalysisData.Objects) &&
    fileAnalysisData.Objects.length > 0;

  // All hooks must be called unconditionally
  const prepareFileInformationCardsArray =
    useCallback((): FileInfoCardData[] => {
      const fileInfoCardsData: FileInfoCardData[] = [];
      if (isSummaryDataAvailable) {
        /*       pushIfExists(
        fileInfoCardsData,
        "Analysis Duration",
        fileAnalysisData.ScanTime ? `${fileAnalysisData.ScanTime} ms` : null,
        <HourglassOutlined />
      );
 */
        pushIfExists(
          fileInfoCardsData,
          "Filename",
          fileAnalysisData.Objects[0].Name,
          <FileOutlined />,
        );
        pushIfExists(
          fileInfoCardsData,
          "Type",
          fileAnalysisData.Objects[0].ObjectType,
          <QuestionCircleOutlined />,
        );
        pushIfExists(
          fileInfoCardsData,
          "Size",
          toHumanReadableSize(fileAnalysisData.Objects[0].ObjectSize),
          //toHumanReadableSize(fileGeneralInfo.size),
          <LayoutOutlined />,
        );
      }
      /* 
    pushIfExists(
      fileInfoCardsData,
      "Analysis Duration",
      fileAnalysisData.ScanTime ? `${fileAnalysisData.ScanTime} ms` : null,
      <HourglassOutlined />,
    );
    */
      return fileInfoCardsData;
    }, [fileAnalysisData, isSummaryDataAvailable]);
  const fileInfoCardsData: FileInfoCardData[] = useMemo(() => {
    const result = prepareFileInformationCardsArray();
    return result;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prepareFileInformationCardsArray]);

  const rating: number = useMemo(() => {
    return getRating(fileAnalysisData);
  }, [fileAnalysisData]);

  const ratingFlag: string = useMemo(() => {
    return fileAnalysisData.SummaryDescription?.RatingFlag ?? "Not Found";
  }, [fileAnalysisData]);

  const verdict: string = useMemo(() => {
    return getVerdictbyFlag(ratingFlag);
  }, [ratingFlag]);

  const verdictSummary: string = summaryTitlesForVerdicts[verdict];

  const sha256: string = useMemo(() => {
    return getSha256(fileAnalysisData);
  }, [fileAnalysisData]);

  function copySha256ToClipboard() {
    // Create a temporary textarea element
    var textarea = document.createElement("textarea");

    // Set its value to the current URL
    textarea.value = sha256;

    // Make the textarea hidden
    textarea.style.position = "absolute";
    textarea.style.left = "-9999px";

    // Append the textarea to the document
    document.body.appendChild(textarea);

    // Select the textarea's content
    textarea.select();

    // Execute the copy command
    document.execCommand("copy");

    // Remove the textarea from the document
    document.body.removeChild(textarea);
  }

  const [, setElapsed] = useState<string>("0 SECONDS");

  function timeSince(date: Date) {
    //@ts-ignore
    var seconds = Math.floor((new Date() - date) / 1000);

    var interval = seconds / 31536000;

    if (interval >= 1) {
      if (interval < 2) {
        return Math.floor(interval) + " YEAR";
      } else {
        return Math.floor(interval) + " YEARS";
      }
    }
    interval = seconds / 2592000;
    if (interval >= 1) {
      if (interval < 2) {
        return Math.floor(interval) + " MONTH";
      } else {
        return Math.floor(interval) + " MONTHS";
      }
    }
    interval = seconds / 86400;
    if (interval >= 1) {
      if (interval < 2) {
        return Math.floor(interval) + " DAY";
      } else {
        return Math.floor(interval) + " DAYS";
      }
    }
    interval = seconds / 3600;

    if (interval >= 1) {
      if (interval < 2) {
        return Math.floor(interval) + " HOUR";
      } else {
        return Math.floor(interval) + " HOURS";
      }
    }
    interval = seconds / 60;
    if (interval >= 1) {
      if (interval < 2) {
        return Math.floor(interval) + " MINUTE";
      } else {
        return Math.floor(interval) + " MINUTES";
      }
    }
    return Math.floor(seconds) + " SECONDS";
  }

  useEffect(() => {
    const interval = setInterval(
      () => setElapsed(timeSince(new Date(fileAnalysisData.TimeStamp))),
      1000,
    );

    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fileAnalysisData.TimeStamp]);
  // TODO: Check which of the above can be used in the template directly from fileAnalysisData, instead
  // of having to create separate variables
  return (
    <>
      {!isSummaryDataAvailable && (
        <div className="top-summary-card-error">
          <h3>No summary available</h3>
          <p>
            This analysis result does not contain file or object summary data.
          </p>
        </div>
      )}
      {isSummaryDataAvailable && (
        <LayoutCard className={`top-summary ${toKebabCase(verdict)}`}>
          {{
            content: (
              <div className="flex-row-with-vertical-alignment">
                <div className="risk-rating-gauge">
                  <ProgRing value={verdict === "Unsupported" ? -1 : rating} />
                </div>
                <div className="summary-divider" />
                <div className="top-summary-card-right-part">
                  <div className="flex-space-between align-items-center">
                    <div className={`verdict-summary ${toKebabCase(verdict)}`}>
                      {verdictSummary}&nbsp;
                      {/* Conditional Tooltip */}
                      {fileAnalysisData.SummaryDescription && (
                        <ToolTip
                          children={
                            <span className="verdict-tooltip-icon">
                              <ExclamationCircleOutlined className="exclamation-circle" />
                            </span>
                          }
                          content={
                            fileAnalysisData.SummaryDescription.Description
                          }
                        />
                      )}
                    </div>
                    <div
                      className={`indicator-verdict-label ${toKebabCase(verdict)}`}
                    >
                      {verdict}
                    </div>
                  </div>
                  <div
                    className="flex-row-with-vertical-alignment"
                    style={{ flexDirection: "row" }}
                  >
                    {/* <ClipboardButton
                      value={sha256}
                      onClick={() => copySha256ToClipboard()}
                    /> */}
                    <span
                      className="icon-with-background clickable"
                      onClick={() => copySha256ToClipboard()}
                    >
                      <CopyOutlined />
                    </span>
                    <div className="sha256-info">
                      <div className="sha256-label">SHA256</div>
                      <div className="sha256-text">{sha256}</div>
                    </div>
                  </div>
                  <div className="top-summary-card-inner-cards-wrapper pills">
                    <div
                      className="pill-div"
                      style={{ borderRight: "1px solid white" }}
                    >
                      <div className="pill">{fileAnalysisData.ScanTime} ms</div>
                      ANALYSIS DURATION
                    </div>
                    <div
                      className="pill-div"
                      style={{
                        paddingRight: "16px",
                      }}
                    >
                      <div className="pill">
                        {new Date(
                          fileAnalysisData.TimeStamp,
                        ).toLocaleDateString("en-GB")}{" "}
                        &nbsp;{" "}
                        {new Date(
                          fileAnalysisData.TimeStamp,
                        ).toLocaleTimeString()}
                      </div>
                      TIMESTAMP
                    </div>
                    {/* Favorite button moved to upload-section in header */}
                  </div>
                </div>
              </div>
            ),
          }}
        </LayoutCard>
      )}

      {/* URL Information replaces File Information if MetaData.HTMLElements exists on Objects[1] */}
      {fileAnalysisData.Objects &&
      fileAnalysisData.Objects[1] &&
      fileAnalysisData.Objects[1].MetaData &&
      fileAnalysisData.Objects[1].MetaData.HTMLElements ? (
        <LayoutCard>
          {{
            content: (
              <>
                <div className="layout-card-header-text flex-row-with-vertical-alignment">
                  <FileUnknownOutlined className="icon-with-background" />
                  URL Information
                </div>
                <UrlHtmlSummaryTab
                  htmlElements={
                    fileAnalysisData.Objects[1].MetaData.HTMLElements
                  }
                />
              </>
            ),
          }}
        </LayoutCard>
      ) : (
        <LayoutCard>
          {{
            content: (
              <>
                <div className="layout-card-header-text flex-row-with-vertical-alignment">
                  <FileUnknownOutlined className="icon-with-background" />
                  File Information
                </div>
                <div className="flex-row-with-vertical-alignment">
                  <div className="top-summary-card-inner-cards-wrapper">
                    {fileInfoCardsData?.map(
                      (fileInfo: FileInfoCardData, index: number) => (
                        <InnerCard
                          className={`file-information-inner-card ${fileInfo.title.toLocaleLowerCase()}`}
                          key={index}
                        >
                          {{
                            header: {
                              icon: fileInfo.icon,
                              isIconHighlighted: true,
                              text: fileInfo.title,
                            },
                            content: fileInfo.contentText,
                          }}
                        </InnerCard>
                      ),
                    )}
                  </div>
                </div>
              </>
            ),
          }}
        </LayoutCard>
      )}
    </>
  );
}

type FileInfoCardData = {
  icon: ReactElement;
  title: string;
  contentText: string;
};

function getRating(fileAnalysisData: any): number {
  return fileAnalysisData.Rating;
}

function getSha256(fileAnalysisData: any): string {
  return fileAnalysisData.Sha256;
}

function pushIfExists(
  dataArray: FileInfoCardData[],
  title: string,
  contentText: string | null,
  icon: ReactElement,
) {
  if (!!contentText) {
    dataArray.push({ icon, title, contentText });
  }
}
